import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DossierVu } from '../partage/api/types';
import { partDuCercle, rangerSelonCeQuiPresse } from '../partage/lesProjets';
import {
  MES_PROJETS,
  RAYON_EN_COURS,
  RAYON_FINIS,
  combienDeCarnets,
  combienDeFinis,
  combienEnCours,
  faitesSurTotal,
} from '../partage/vocabulaire';
import { teinteDuProjet } from '../apparence';
import { HautDuPouce } from '../partage/HautDuPouce';
import './TousLesProjets.css';

/** Trois carnets par rangée, une étagère dessous (maquette `app-au-telephone.html`, `projets()`). */
const PAR_RANGEE = 3;

/**
 * Tous les projets, au téléphone : des carnets en tranche sur une étagère (chantier
 * telephone-projet, 01, maquette `app-au-telephone.html`, `projets()`, `.tranche`, `.prog`,
 * `.etagere`, `.titre-s`).
 *
 * « Mes projets » en titre, « En cours » puis un carnet par projet non fini : sa couleur sur le
 * bord et en ruban marque-page, son nom à la main, la prochaine chose à faire dessous (rien si le
 * serveur n'en donne pas), sa barre d'avancement et « faites/total », jamais de pourcentage. Une
 * étagère après chaque rangée de trois. Les projets finis sous « Finis · N carnets », repliés :
 * un toucher les déplie. Les carnets sont rangés selon ce qui presse (`rangerSelonCeQuiPresse`).
 * Toucher un carnet entre dans le projet. L'ordinateur a sa propre étagère (`Dossiers.tsx`).
 *
 * Couché (`couche`, chantier telephone-couche-suite, 02, maquette `cProjets()`) : les mêmes
 * projets, dans le même ordre, en carnets de face sur une rangée qui défile de côté ; « N finis »,
 * en haut, ajoute les finis au bout de la rangée, comme « Finis » les déplie droit.
 */
export function TousLesProjets({ dossiers, couche = false }: { dossiers: readonly DossierVu[]; couche?: boolean }) {
  const naviguer = useNavigate();
  const [finisOuverts, setFinisOuverts] = useState(false);
  const rangee = rangerSelonCeQuiPresse(dossiers);
  const enCours = rangee.filter((d) => !d.fini);
  const finis = rangee.filter((d) => d.fini);
  const entrer = (dossier: DossierVu) => naviguer(`/feuille/${dossier.id}`);

  if (couche) {
    const montres = finisOuverts ? [...enCours, ...finis] : enCours;
    return (
      <div className="tous-projets tous-projets--couche">
        <HautDuPouce
          couche
          titre={MES_PROJETS}
          apres={
            <span className="haut-pouce-apres tous-projets-compte">
              {combienEnCours(enCours.length)}
              {finis.length > 0 && (
                <>
                  {' · '}
                  <button
                    type="button"
                    className="tous-projets-deplier"
                    aria-expanded={finisOuverts}
                    onClick={() => setFinisOuverts((ouverts) => !ouverts)}
                  >
                    {combienDeFinis(finis.length)}
                  </button>
                </>
              )}
            </span>
          }
        />
        <div className="couvs-projets">
          {montres.map((dossier) => (
            <CarnetDeFace key={dossier.id} dossier={dossier} onEntrer={() => entrer(dossier)} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="tous-projets">
      <HautDuPouce titre={MES_PROJETS} />
      {enCours.length > 0 && (
        <>
          <h2 className="tous-projets-titre">{RAYON_EN_COURS}</h2>
          <Etageres dossiers={enCours} onEntrer={entrer} />
        </>
      )}
      {finis.length > 0 && (
        <>
          <h2 className="tous-projets-titre tous-projets-titre--finis">
            <button
              type="button"
              className="tous-projets-deplier"
              aria-expanded={finisOuverts}
              onClick={() => setFinisOuverts((ouverts) => !ouverts)}
            >
              {RAYON_FINIS}
              <small>{combienDeCarnets(finis.length)}</small>
            </button>
          </h2>
          {finisOuverts && <Etageres dossiers={finis} onEntrer={entrer} />}
        </>
      )}
    </div>
  );
}

/** Les carnets par rangées de trois, une étagère sous chacune. */
function Etageres({ dossiers, onEntrer }: { dossiers: readonly DossierVu[]; onEntrer: (dossier: DossierVu) => void }) {
  const rangees: DossierVu[][] = [];
  for (let i = 0; i < dossiers.length; i += PAR_RANGEE) rangees.push(dossiers.slice(i, i + PAR_RANGEE));
  return (
    <>
      {rangees.map((rangee) => (
        <div key={rangee[0]!.id} className="tous-projets-rangee">
          {rangee.map((dossier) => (
            <CarnetEnTranche key={dossier.id} dossier={dossier} onEntrer={() => onEntrer(dossier)} />
          ))}
          <div className="tous-projets-etagere" aria-hidden="true" />
        </div>
      ))}
    </>
  );
}

/** Un carnet en tranche (`.tranche`) : le bord et le ruban à la couleur du projet. */
function CarnetEnTranche({ dossier, onEntrer }: { dossier: DossierVu; onEntrer: () => void }) {
  const { faites, total } = dossier.progression;
  const part = `${partDuCercle(faites, total) * 100}%`;

  return (
    <button
      type="button"
      className="tranche-projet"
      data-tuto="dossier"
      data-dossier={dossier.id}
      style={{ ['--teinte' as string]: teinteDuProjet(dossier.couleur) }}
      onClick={onEntrer}
    >
      <span className="tranche-projet-mots">
        <b className="tranche-projet-nom" data-tuto="dossier-titre">
          {dossier.titre}
        </b>
        {dossier.prochaine && (
          <small className="tranche-projet-suite" data-tuto="dossier-maintenant">
            {dossier.prochaine.titre}
          </small>
        )}
        <span className="tranche-projet-prog" aria-hidden="true">
          <i style={{ width: part }} />
        </span>
      </span>
      <span className="tranche-projet-compte" data-tuto="dossier-acquis">
        {faitesSurTotal(faites, total)}
      </span>
    </button>
  );
}

/**
 * Un carnet de face, couché (`.couv`, `.elast`, `.etiq`, `.couv-b`, `.prog`) : la couverture à la
 * couleur du projet, l'élastique, l'étiquette (le nom, la prochaine chose), la barre d'avancement
 * et « faites/total ». Les mêmes chiffres que le carnet en tranche.
 */
function CarnetDeFace({ dossier, onEntrer }: { dossier: DossierVu; onEntrer: () => void }) {
  const { faites, total } = dossier.progression;
  const part = `${partDuCercle(faites, total) * 100}%`;

  return (
    <button
      type="button"
      className={`couv-projet${dossier.fini ? ' couv-projet--fini' : ''}`}
      data-tuto="dossier"
      data-dossier={dossier.id}
      style={{ ['--teinte' as string]: teinteDuProjet(dossier.couleur) }}
      onClick={onEntrer}
    >
      <span className="couv-projet-elastique" aria-hidden="true" />
      <span className="couv-projet-etiquette">
        <b className="couv-projet-nom" data-tuto="dossier-titre">
          {dossier.titre}
        </b>
        {dossier.prochaine && (
          <small className="couv-projet-suite" data-tuto="dossier-maintenant">
            {dossier.prochaine.titre}
          </small>
        )}
      </span>
      <span className="couv-projet-bas">
        <span className="couv-projet-prog" aria-hidden="true">
          <i style={{ width: part }} />
        </span>
        <span data-tuto="dossier-acquis">{faitesSurTotal(faites, total)}</span>
      </span>
    </button>
  );
}
